import { type ApolloCache } from '@apollo/client'
import { formatChips } from '@polf/shared/locale'
import { type ReactNode, useMemo, useState } from 'react'

import {
  BeatTheProEntryStateEnum,
  BeatTheProPlayersDocument,
  type BeatTheProPlayersQuery,
  type BtpPlayerFragment,
  useBeatTheProContextQuery,
  useBeatTheProPlayersQuery,
  useBtpDeleteBetMutation,
  useBtpPlaceBetMutation,
  useBtpSetResultMutation,
} from '../../__generated__/hooks'
import Toggle from '../../components/Leaderboard/Toggle'
import BtpPlayerRow from './BtpPlayerRow'
import PlaceBetSheet from './PlaceBetSheet'
import SetResultSheet from './SetResultSheet'

// Write the mutation's returned player back into the players list so the UI
// reflects a placed/settled/deleted bet immediately. The mutations also
// refetch, but object-form refetchQueries can lag a render (same Apollo
// gotcha the Score page works around with an explicit cache write) — this
// makes the update deterministic so no browser refresh is needed.
const writePlayer = (
  cache: ApolloCache<unknown>,
  player: BtpPlayerFragment | null | undefined
) => {
  if (!player) return
  const existing = cache.readQuery<BeatTheProPlayersQuery>({
    query: BeatTheProPlayersDocument,
  })
  if (!existing) return
  cache.writeQuery<BeatTheProPlayersQuery>({
    query: BeatTheProPlayersDocument,
    data: {
      currentOpponentPlayers: existing.currentOpponentPlayers.map((p) =>
        p.id === player.id ? player : p
      ),
    },
  })
}

/**
 * Beat the PRO — the pro's live betting console (opponent-key route
 * /b/:opponentKey/beatthepro). The pro places a bet per player, then marks
 * the result. Read-only when the event is finished (spectator).
 *
 * The Spillere view is sectioned by the pro's two jobs: "Aktive bets" (placed,
 * undecided — the settle list) sits on top, then "Ingen bet ennå" (the
 * take-bets list); "Avgjort" is revealed by the show-completed toggle. The
 * Flights view is a flat roster grouped by flight number.
 */

type View = 'players' | 'flights'

const isDecided = (p: BtpPlayerFragment) => {
  const s = p.entry?.beatTheProExtra?.state
  return (
    s === BeatTheProEntryStateEnum.Won || s === BeatTheProEntryStateEnum.Lost
  )
}
const hasActiveBet = (p: BtpPlayerFragment) =>
  p.entry?.beatTheProExtra?.state === BeatTheProEntryStateEnum.Undecided

const betLabelFor = (p: BtpPlayerFragment): string => {
  const extra = p.entry?.beatTheProExtra
  if (!extra) return ''
  const stake = extra.allIn ? 'All-in' : `Bet: ${formatChips(extra.bet)} chips`
  if (extra.state === BeatTheProEntryStateEnum.Undecided)
    return `${stake} · venter`
  return stake
}

const BeatThePro = () => {
  const [view, setView] = useState<View>('players')
  const [showCompleted, setShowCompleted] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)

  const { data: ctxData } = useBeatTheProContextQuery({
    fetchPolicy: 'cache-and-network',
  })
  const { data, loading, error } = useBeatTheProPlayersQuery({
    fetchPolicy: 'cache-and-network',
    // Bets + pro results land from other devices (fellow players betting, the
    // pro settling). Poll so the list stays live without a reload; own actions
    // still update instantly via the mutation cache writes below.
    pollInterval: 10_000,
  })

  const [placeBet, { loading: placing }] = useBtpPlaceBetMutation({
    refetchQueries: [{ query: BeatTheProPlayersDocument }],
    update: (cache, { data }) =>
      writePlayer(cache, data?.currentOpponentHoleCompetitionEntryAdd),
  })
  const [setResult, { loading: settingResult }] = useBtpSetResultMutation({
    refetchQueries: [{ query: BeatTheProPlayersDocument }],
    update: (cache, { data }) =>
      writePlayer(cache, data?.currentOpponentHoleCompetitionEntrySetResult),
  })
  const [deleteBet, { loading: deleting }] = useBtpDeleteBetMutation({
    refetchQueries: [{ query: BeatTheProPlayersDocument }],
    update: (cache, { data }) =>
      writePlayer(cache, data?.currentOpponentHoleCompetitionEntryDelete),
  })

  const opponent = ctxData?.currentOpponent
  const comp = opponent?.holeCompetition
  const readOnly = comp?.polfEvent?.isReadOnly ?? false

  const all = useMemo(
    () => data?.currentOpponentPlayers ?? [],
    [data?.currentOpponentPlayers]
  )
  const sorted = useMemo(
    () => [...all].sort((a, b) => a.name.localeCompare(b.name)),
    [all]
  )
  const activeBets = useMemo(() => sorted.filter(hasActiveBet), [sorted])
  const noBet = useMemo(() => sorted.filter((p) => !p.entry), [sorted])
  const decided = useMemo(() => sorted.filter(isDecided), [sorted])
  // Flights view roster respects the same show-completed reveal.
  const flightRoster = showCompleted
    ? sorted
    : sorted.filter((p) => !isDecided(p))

  const active = activeId ? all.find((p) => p.id === activeId) ?? null : null
  const close = () => setActiveId(null)

  const onPlace = (arg: { bet: number; allIn: boolean }) => {
    if (!active) return
    placeBet({
      variables: {
        dto: {
          polfEventPlayerId: active.id,
          value: 0,
          beatTheProExtra: { bet: arg.bet, allIn: arg.allIn },
        },
      },
    })
      .then(close)
      .catch(() => {
        /* surfaced via Apollo error link */
      })
  }
  const onResult = (state: BeatTheProEntryStateEnum) => {
    if (!active?.entry) return
    setResult({
      variables: { playerId: active.id, entryId: active.entry.id, state },
    })
      .then(close)
      .catch(() => {})
  }
  const onDelete = () => {
    if (!active?.entry) return
    deleteBet({ variables: { playerId: active.id, entryId: active.entry.id } })
      .then(close)
      .catch(() => {})
  }

  if (error) {
    return (
      <main className="min-h-dvh bg-cream-bg flex flex-col items-center justify-center px-6 text-center">
        <p className="text-flag">Fant ikke Beat the PRO-utfordringen.</p>
      </main>
    )
  }

  const playersEmpty =
    activeBets.length === 0 &&
    noBet.length === 0 &&
    (!showCompleted || decided.length === 0)

  return (
    <main className="min-h-dvh bg-cream-bg">
      <header
        className="bg-green-900 text-paper px-4 pb-4"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
      >
        <p className="uppercase tracking-caps text-[11px] font-bold text-green-100">
          Beat the PRO
        </p>
        <h1 className="font-display font-bold text-2xl leading-tight mt-0.5">
          {opponent?.name ?? '…'}
        </h1>
        {comp?.polfEvent?.name ? (
          <p className="text-sm text-green-100 mt-0.5">{comp.polfEvent.name}</p>
        ) : null}
        {comp ? (
          <div className="mt-3 flex items-stretch gap-3">
            <div className="rounded-lg bg-white/10 px-3 py-2 text-center shrink-0">
              <div className="text-[10px] uppercase tracking-caps text-green-100">
                Hull
              </div>
              <div className="font-mono num text-2xl font-bold leading-none mt-0.5">
                {comp.hole.number}
              </div>
              <div className="text-[10px] text-green-100 mt-0.5">
                Par {comp.hole.par}
              </div>
            </div>
            {comp.description ? (
              <p className="flex-1 min-w-0 text-sm leading-snug self-center">
                {comp.description}
              </p>
            ) : null}
          </div>
        ) : null}
      </header>

      {readOnly ? (
        <div className="bg-cream-card border-b border-ink-100 px-4 py-2 text-center text-sm text-ink-700">
          Turneringen er ferdig – bets er låst.
        </div>
      ) : null}

      <div className="sticky top-0 z-10 bg-cream-bg px-4 py-3 flex items-center justify-between gap-2">
        <Toggle
          options={[
            { value: 'players', label: 'Spillere' },
            { value: 'flights', label: 'Flights' },
          ]}
          value={view}
          onChange={setView}
        />
        <button
          type="button"
          aria-pressed={showCompleted}
          onClick={() => setShowCompleted((v) => !v)}
          className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
            showCompleted
              ? 'bg-green-700 text-paper'
              : 'bg-ink-100 text-ink-500'
          }`}
        >
          Inkl. avgjorte
        </button>
      </div>

      <section className="px-4 pb-8">
        {loading && all.length === 0 ? (
          <p className="text-center text-ink-500 py-8">Laster…</p>
        ) : view === 'flights' ? (
          <FlightsBody
            roster={flightRoster}
            readOnly={readOnly}
            onTap={setActiveId}
          />
        ) : playersEmpty ? (
          <p className="text-center text-ink-500 py-8">Ingen spillere ennå.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <Section
              title="Aktive bets"
              players={activeBets}
              readOnly={readOnly}
              onTap={setActiveId}
            />
            <Section
              title="Ingen bet ennå"
              players={noBet}
              readOnly={readOnly}
              onTap={setActiveId}
            />
            {showCompleted && (
              <Section
                title="Avgjort"
                players={decided}
                readOnly={readOnly}
                onTap={setActiveId}
              />
            )}
          </div>
        )}
      </section>

      {active && !active.entry && !readOnly ? (
        <PlaceBetSheet
          open
          playerName={active.name}
          saving={placing}
          onClose={close}
          onSave={onPlace}
        />
      ) : null}
      {active && active.entry ? (
        <SetResultSheet
          open
          playerName={active.name}
          betLabel={betLabelFor(active)}
          currentState={
            active.entry.beatTheProExtra?.state ??
            BeatTheProEntryStateEnum.Undecided
          }
          saving={settingResult || deleting}
          onClose={close}
          onSetResult={onResult}
          onDelete={onDelete}
        />
      ) : null}
    </main>
  )
}

const SectionHeader = ({ children }: { children: ReactNode }) => (
  <div className="text-[10px] uppercase tracking-caps text-ink-500 px-1">
    {children}
  </div>
)

const Section = ({
  title,
  players,
  readOnly,
  onTap,
}: {
  title: string
  players: BtpPlayerFragment[]
  readOnly: boolean
  onTap: (id: string) => void
}) => {
  if (players.length === 0) return null
  return (
    <div className="flex flex-col gap-2">
      <SectionHeader>
        {title} · {players.length}
      </SectionHeader>
      {players.map((p) => (
        <BtpPlayerRow
          key={p.id}
          player={p}
          readOnly={readOnly}
          onTap={() => onTap(p.id)}
        />
      ))}
    </div>
  )
}

const FlightsBody = ({
  roster,
  readOnly,
  onTap,
}: {
  roster: BtpPlayerFragment[]
  readOnly: boolean
  onTap: (id: string) => void
}) => {
  const groups = useMemo(() => {
    const byFlight = new Map<number | null, BtpPlayerFragment[]>()
    for (const p of roster) {
      const key = p.flight?.flightNumber ?? null
      const arr = byFlight.get(key) ?? []
      arr.push(p)
      byFlight.set(key, arr)
    }
    return [...byFlight.entries()].sort((a, b) => {
      if (a[0] == null) return 1
      if (b[0] == null) return -1
      return a[0] - b[0]
    })
  }, [roster])

  if (roster.length === 0) {
    return <p className="text-center text-ink-500 py-8">Ingen spillere ennå.</p>
  }
  return (
    <div className="flex flex-col gap-4">
      {groups.map(([flightNumber, list]) => (
        <div key={flightNumber ?? 'none'} className="flex flex-col gap-2">
          <SectionHeader>
            {flightNumber == null ? 'Uten flight' : `Flight ${flightNumber}`}
          </SectionHeader>
          {list.map((p) => (
            <BtpPlayerRow
              key={p.id}
              player={p}
              readOnly={readOnly}
              onTap={() => onTap(p.id)}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export default BeatThePro
